import { FC, Suspense, useEffect } from 'react'
import TagManager from 'react-gtm-module'
import { getTreatments } from '@splitsoftware/splitio-redux'
import { ITemplate } from '../interfaces/templates'
import Cart from '../templates/Cart'
import Catalog from '../templates/Catalog'
import Categories from '../templates/Categories'
import Default from '../templates/Default'
import Filters from '../templates/Filters'
import CategoriesNav from '../templates/CategoriesNav'
import { useAppDispatch, useAppSelector } from '../hooks'
import { SPLIT_NAMES } from '../consts/splitio'
import Summary from '../templates/Summary'
import ProductDetail from '../templates/ProductDetail'
import YaloAnalytics from '../modules/analytics'
import CatalogFixed from '../templates/CatalogFixed'
import OrderHistory from '../templates/OrderHistory'
import OrderDetail from '../templates/OrderDetail'
import OrderConfirmation from '../templates/OrderConfirmation'
import DeliveryDatePicker from '../templates/DeliveryDatePicker'
import PaymentMethodSelector from '../templates/PaymentMethodSelector'
import {
  GOOGLE_TAG_MANAGER_DATA_LAYER_NAME,
  GOOGLE_TAG_LOAD_EVENT,
} from '../consts/defaultConfigValues/defaultConstants'
import { eventNames, TEventNames } from '../modules/interfaces'

/**
 * Consumes the `default:` arm's narrowed `type` so the narrowing is checked.
 *
 * A function rather than an unused `const` so there is nothing for lint to
 * flag, and one that ignores its argument so there is nothing at runtime.
 */
const assertEveryContainerTypeHandled = (_unhandled: undefined) => undefined

const BaseContainer: FC<ITemplate> = ({ container, ...props }) => {
  const dispatch = useAppDispatch()
  const { type } = container
  const sessionData = useAppSelector((state) => state.defaultSlice.sessionData)
  const { sessionId = '', config } = useAppSelector(
    (state) => state.defaultSlice,
  )
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const [phoneNumber] = sessionData?.customer?.phoneNumber || []
  const email = sessionData?.customer?.email
  const contextAttributes = {
    email,
    phoneNumber,
  }

  dispatch(
    getTreatments({
      splitNames: SPLIT_NAMES,
      attributes: contextAttributes,
      key: storeName || '',
    }),
  )

  const getTemplate = () => {
    switch (type) {
      case 'cart':
        return <Cart container={container} {...props} />
      case 'catalog':
        return <Catalog container={container} {...props} />
      case 'filters':
        return <Filters container={container} {...props} />
      case 'categories':
        return <Categories container={container} {...props} />
      case 'summary':
        return <Summary container={container} {...props} />
      case 'categoriesnav':
        return <CategoriesNav container={container} {...props} />
      case 'productdetail':
        return <ProductDetail container={container} {...props} />
      case 'catalogFixed':
        return <CatalogFixed container={container} {...props} />
      case 'orderHistory':
        return <OrderHistory container={container} {...props} />
      case 'orderDetail':
        return <OrderDetail container={container} {...props} />
      case 'orderConfirmation':
        return <OrderConfirmation container={container} {...props} />
      case 'deliveryDatePicker':
        return <DeliveryDatePicker container={container} {...props} />
      case 'paymentMethodSelector':
        return <PaymentMethodSelector container={container} {...props} />
      default:
        // The compile-time link the `switch` otherwise lacks: with every
        // member of `IContainersBase['type']` cased above, `type` narrows to
        // `undefined` here — so adding a container type without adding its
        // case fails this call and names the type, instead of silently
        // rendering `Default`. A runtime `type` outside the union still lands
        // here on purpose, because it comes from remote JSON.
        assertEveryContainerTypeHandled(type)
        return <Default container={container} {...props} />
    }
  }

  const trackPageView = (
    eventName: TEventNames,
    addToEventsBatch: boolean = true,
  ) => {
    YaloAnalytics.trackPageView(
      {
        pageData: {
          isModal: false,
        },
        eventName,
        viewName: container.title,
      },
      addToEventsBatch,
    )
  }

  useEffect(() => {
    const tagManagerArgs = {
      dataLayer: {
        account_name: sessionData?.ownerInfo?.slug,
        storefront_name: storeName,
        store_id: sessionData?.customer?.code,
        event: GOOGLE_TAG_LOAD_EVENT,
      },
      dataLayerName: GOOGLE_TAG_MANAGER_DATA_LAYER_NAME,
    }

    TagManager.dataLayer(tagManagerArgs)
  }, [])

  useEffect(() => {
    YaloAnalytics.trackEventsURL = config?.options?.trackEventsURL || ''
    YaloAnalytics.trackEvents = !!config?.options?.trackEventsURL || false
    window.addEventListener('beforeunload', (event) => {
      trackPageView(eventNames.webviewClosed, false)
    })
  }, [])

  useEffect(() => {
    YaloAnalytics.setBaseEventData({
      phoneNumber,
      sessionId,
      storefront: storeName || '',
      extraIds: sessionData?.extraIds || [],
    })
    trackPageView(eventNames.pageView)
  }, [
    phoneNumber,
    sessionId,
    container.title,
    storeName,
    sessionData?.extraIds,
  ])

  return <Suspense fallback={<div />}>{getTemplate()}</Suspense>
}

export default BaseContainer
